import {
  Skeleton,
  Box,
  Image,
  Text,
  Heading,
  Divider,
  Grid,
  Alert,
  Tag,
  Flex,
  Button
} from "@chakra-ui/react";
import { TextWithLabel } from "components/Atomos/Texts/TextWithlabel";
import { SideAreaMap } from "components/Moleculas/Map/Side/SideAreaMap";
import { useEffect } from "react";
import { useAppDispatch, useTypedSelector } from "store/hooks";
import { encerrarManifestacao } from "store/modules/ouvidoria/manifestacoes/actions/encerrarManifestacao";
import { obterManifestacaoPorId } from "store/modules/ouvidoria/manifestacoes/actions/obterManifestacaoPorId";
import { useVerManifestacao } from "../contexts/VerManifestacaoProvider";
import { CommentCard } from "./CommentCard";
import { CommentForm } from "./CommentForm";

export function SideVerManifestacao() {
  const { idManifestacao, setIdManifestacao } = useVerManifestacao();
  const dispatch = useAppDispatch();

  const { isRequesting: isRequestingEncerrar } = useTypedSelector(
    (state) => state.ouvidoria.manifestacoes.encerrarManifestacao
  );

  function encerrar(idManifestacao: string) {
    dispatch(
      encerrarManifestacao({
        idManifestacao: idManifestacao,
        nota: 5,
        onSuccess: () => {
          load(idManifestacao);
        }
      })
    );
  }

  function load(idManifestacao: string) {
    dispatch(obterManifestacaoPorId({ id: idManifestacao }));
  }

  function close() {
    setIdManifestacao(null);
  }

  useEffect(() => {
    if (idManifestacao) {
      load(idManifestacao);
    }
  }, [idManifestacao]);

  const { successPayload: manifestacao, isRequesting } = useTypedSelector(
    (state) => state.ouvidoria.manifestacoes.obterManifestacaoPorId
  );

  return (
    <SideAreaMap isOpen={idManifestacao !== null} onClose={close} zIndex={1000}>
      {isRequesting && (
        <Box p={4}>
          <Skeleton height="500px" />
        </Box>
      )}
      {manifestacao && (
        <>
          <Box pt={6}>
            {manifestacao.imagem && (
              <Image
                mb="4"
                src={manifestacao.imagem}
                alt="Imagem da manifestação"
              />
            )}
            <Heading size="md">{manifestacao.titulo}</Heading>
            <Box alignItems="start">
              <Text size="sm">Protocolo: {manifestacao.protocolo}</Text>
              <Text size="sm">{manifestacao.tipoManifestacao}</Text>
              {manifestacao && manifestacao.status === "Encerrada" && (
                <Tag colorScheme="orange">Encerrada</Tag>
              )}
            </Box>
            <Text mt="2">{manifestacao.descricao}</Text>
            <Text color="gray.500" mt="1" fontSize="sm">
              Criado em {manifestacao.dataAberturaFormatada}
            </Text>
          </Box>
          <Divider py={2} />
          <Grid templateColumns="1fr 1fr" gap={4} py={6}>
            <TextWithLabel
              label="Nome"
              value={manifestacao.nomeUsuario || "-"}
            />
            <TextWithLabel label="CPF" value={manifestacao.cpfUsuario || "-"} />
            <TextWithLabel
              label="Email"
              value={manifestacao.emailUsuario || "-"}
            />
            <TextWithLabel
              label="Telefone"
              value={manifestacao.telefoneUsuario || "-"}
            />
            <TextWithLabel
              label="Endereço"
              value={manifestacao.enderecoUsuario?.logradouro || "-"}
            />
          </Grid>
          <Divider py={2} />
          <Box pt={6}>
            <Text fontWeight="bold" mb="2">
              Histórico de mensagens
            </Text>
            {manifestacao.mensagens.length === 0 && (
              <Alert>Nenhum mensagem enviada</Alert>
            )}
            <Box>
              {manifestacao.mensagens.map((mensagem) => (
                <CommentCard
                  key={mensagem.idMensagem}
                  comment={mensagem.texto}
                  senderName={mensagem.nomeUsuario}
                  sentDate={mensagem.horario}
                  isAuthor={!mensagem.usuarioManifestante}
                />
              ))}
            </Box>
            {manifestacao && manifestacao.status !== "Encerrada" && (
              <>
                <Box mt="4">
                  <CommentForm
                    idManifestacao={manifestacao.idManifestacao}
                    onSuccess={() => {
                      load(manifestacao.idManifestacao);
                    }}
                    onClose={close}
                  />
                </Box>
                <Flex justifyContent="flex-end" w="full">
                  <Button
                    mt="6"
                    colorScheme="red"
                    isLoading={isRequestingEncerrar}
                    onClick={() => encerrar(manifestacao.idManifestacao)}
                  >
                    Encerrar manifestação
                  </Button>
                </Flex>
              </>
            )}
          </Box>
        </>
      )}
    </SideAreaMap>
  );
}
